import { StyleSheet } from "react-native";
import type { UiTheme } from "../theme";

// Styles for the ProgressView footer — the total/7d/30d/last-session stat
// grid + the weekly-frequency bar chart. Lifted from mobile's
// progressScreenStyles (the tag-card + scroll-container rules stay with the
// host mount). The host owns the scroll container + its padding.
export function makeProgressViewStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    // Wide (desktop console) 2-column grid for the supporting panels. Strict
    // two-up: each cell is 48% wide, space-between sets the gutter, rowGap
    // spaces the rows. A lone trailing panel sits left at 48%. Mobile never
    // hits this (it's always narrow → the panels stack in one column).
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      rowGap: s.s4,
    },
    gridCell: { width: "48%" },
    // PG-1 §3 "More detail" fold — the box/header/chevron/divided-body chrome is
    // the shared <DisclosureCard> primitive; only the header TEXT (title + sub)
    // and the body's per-surface spacing live here. The deep-cut panels fold
    // away so the glanceable surface (alerts + coverage + strength + rhythm)
    // reads without the wall-of-panels.
    detailsEyebrow: {
      color: p.ink,
      fontSize: t.sizes.md,
      fontWeight: "600",
    },
    detailsSub: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // Merged over the primitive body (which already gives the top divider +
    // horizontal padding): the analytical panels need vertical breathing room
    // + a gap between them / the records card.
    detailsBody: {
      paddingTop: s.s4,
      paddingBottom: s.s4,
      gap: s.s4,
    },
    statGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s3,
    },
    stat: {
      flex: 1,
      minWidth: "45%",
      flexDirection: "column",
      gap: s.s2,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    statLabel: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    statValue: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: "600",
      fontFamily: t.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    cardTitle: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    cardHelp: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    barChart: {
      flexDirection: "row",
      alignItems: "flex-end",
      height: 120,
      gap: 4,
      paddingTop: s.s2,
    },
    barColumn: {
      flex: 1,
      alignItems: "center",
      justifyContent: "flex-end",
      height: "100%",
    },
    bar: {
      width: "70%",
      borderRadius: 2,
    },
    barLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontFamily: t.fonts.mono,
      fontVariant: ["tabular-nums"],
      marginTop: 2,
    },
  });
}
